// IMPORT REACT
import React, { useContext, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

// IMPORT PACKGES
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";

//IMPORT UI COMPONENTS
import {
  Box,
  Grid,
  FormControl,
  FormLabel,
  Typography,
  Button,
} from "@mui/material";
import { FormProvider, useForm } from "react-hook-form";
import { LoadingButton } from "@mui/lab";
import CustomBreadcrumbs from "../../../components/BreadcrumbsComponent/CustomBreadcrumbs";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

// IMPORT SERVICE
import { PreferencesContext } from "../../../PreferenceContext";
import { AlertType } from "../../../services/models/AlertType";
import { EncryptDecryptService } from "../../../services/common/EncryptionService";
import SkeletonWrapper from "../../../components/Skeleton/SkeletonWrapper";
import { CustomTextField } from "../../../components/CustomFields/TextField";
import {
  CreateUserGroup,
  GetRolesDetail,
  UpdateUserGroup,
} from "../../../services/RolesService";

export default function UserGroupSave(Props: any) {
  const { isForPopUp = false, handleClosePopUp, roleName } = Props;

  // SCHEMA FOR FORM
  interface formInitialValueTypes {
    roleId: number;
    roleCode: string;
    roleName: string;
    isDeleted: boolean;
  }

  // DEFAULT SCHEMA VALUE FOR FORM
  const initUserGroupData: formInitialValueTypes = {
    roleId: 0,
    roleCode: "",
    roleName: isForPopUp ? roleName?.toUpperCase() : "",
    isDeleted: false,
  };

  const navigate = useNavigate();
  const edService = new EncryptDecryptService();
  const { userContext, updateUserContext } = useContext(PreferencesContext);
  function useQuery() {
    const { search } = useLocation();
    return useMemo(() => new URLSearchParams(search), [search]);
  }

  // GETTTING PARAMS
  let urlParams = useQuery();
  let curRawUserGroupId = urlParams.get("q");
  let curUserGroupId = 0;
  if (curRawUserGroupId !== null && curRawUserGroupId !== undefined) {
    let decId = edService.decrypt(curRawUserGroupId);
    if (
      decId !== "" &&
      decId !== null &&
      decId !== undefined &&
      isForPopUp == false
    ) {
      curUserGroupId = +decId;
    }
  }

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [showSkeleton, setShowSkeleton] = useState<boolean>(true);

  //SCHEMA VALIDATION
  const validationSchema = Yup.object().shape({
    roleId: Yup.number().nullable(),
    roleCode: Yup.string().nullable(),
    roleName: Yup.string()
      .required("User Group Name is required")
      .max(100, "User Group Name cannot exceed 100 characters"),
  });

  // FORM HOOK
  const methods = useForm({
    resolver: yupResolver(validationSchema), // IGNORE THIS ERROR
    defaultValues: initUserGroupData, // DEFAULT VALUE -- DON'T CREATE STATE FOR IT -- STATE CAN INCREASE SITE LOAD
  });

  // SAVE AND UPDATE USER GROUP
  const saveUserGroup = async (value: formInitialValueTypes) => {
    try {
      let result: any = "";
      let formData = {
        roleId: value.roleId,
        consumerId: userContext.consumerId.toString(),
        orgId: userContext.organizationId.toString(),
        branchId: userContext.branchId.toString(),
        roleCode: value?.roleCode.trim() || "",
        roleName: value?.roleName.trim() || "",
        isDeleted: value.isDeleted,
        dataManagedBy: userContext.loginDetail.userId,
      };
      if (curUserGroupId > 0) {
        result = await UpdateUserGroup(formData);
      } else {
        result = await CreateUserGroup(formData);
      }
      if (result?.data) {
        if (!isForPopUp) {
          navigate(-1);
        } else {
          if (result?.data) {
            handleClosePopUp("GROUP_CREATE", result?.data);
          }
        }
        updateUserContext({
          ...userContext,
          isAlert: true,
          alertMessage: result?.message,
          alertType: AlertType.Success,
        });
      } else {
        updateUserContext({
          ...userContext,
          isAlert: true,
          alertMessage: "Something went wrong.",
          alertType: AlertType.Error,
        });
      }
    } catch (e: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  };

  // GET USER GROUP DETAIL
  const getRolesDetailById = async () => {
    let role: any;
    let params = new URLSearchParams();
    params.append("RolesId", curUserGroupId.toString());
    params.append("ConsumerId", userContext.consumerId.toString());
    params.append("OrgId", userContext.organizationId.toString());
    params.append("BranchId", userContext.branchId.toString());
    try {
      const res: any = await GetRolesDetail(params);
      role = res.data.data;
      methods.reset({
        ...initUserGroupData,
        roleId: role?.roleId || 0,
        roleCode: role?.roleCode || "",
        roleName: role?.roleName || "",
      });
      return role;
    } catch (e: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  };

  // ON SUBMIT OF FORM
  const onSubmit = async (data: any) => {
    saveUserGroup(data);
  };

  // HANDLE BACK
  const handleBack = () => {
    if (isForPopUp) {
      handleClosePopUp("CLOSE");
    } else {
      navigate(-1);
    }
  };

  // FETCH DATA
  const fetchData = async () => {
    try {
      if (curUserGroupId > 0) {
        await getRolesDetailById();
      }
      setIsLoading(false);
    } catch (e) {
      setIsLoading(false);
      console.log(e);
    }
  };

  // USEEFFECTS
  useEffect(() => {
    setIsLoading(true);
    fetchData();
  }, [curUserGroupId]);

  // HANDLE DELAY FOR SKELETON
  useEffect(() => {
    if (!isLoading) {
      const timeout = setTimeout(() => setShowSkeleton(false), 50); // Delay unmounting the Skeleton
      return () => clearTimeout(timeout);
    } else {
      setShowSkeleton(true);
    }
  }, [isLoading]);

  return (
    <Box className="main-container">
      {isForPopUp === false && (
        <Grid container className="page-header">
          <Grid item xs={6}>
            <Box sx={{ display: "flex", height: "100%", alignItems: "center" }}>
              <CustomBreadcrumbs />
            </Box>
          </Grid>
          <Grid item xs={6}>
            <Box className="page-header-icon" sx={{ textAlign: "right" }}>
              <Button
                color="primary"
                variant="outlined"
                onClick={handleBack}
                startIcon={<ArrowBackIcon />}
              >
                Back
              </Button>
            </Box>
          </Grid>
        </Grid>
      )}
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)} action="">
          <Grid container gap={2} mt={1}>
            <Grid item xs={12} className="group-container">
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth>
                    <FormLabel
                      className={`fileds-label-text  ${
                        methods.formState.errors.roleCode
                          ? "validation-message"
                          : ""
                      }`}
                    >
                      Role Code
                    </FormLabel>
                    <SkeletonWrapper
                      isLoading={showSkeleton}
                      className="skeleton-input"
                    >
                      <CustomTextField
                        placeholder=""
                        autoCapitalize="none"
                        type="text"
                        fullWidth
                        size="small"
                        {...methods.register("roleCode", {
                          required: true,
                          onChange: (e) => {
                            const input = e.target;
                            const selectionStart = input.selectionStart;
                            const selectionEnd = input.selectionEnd;

                            // CONVERT INPUT INTO UPPERCASE LATTERS
                            e.target.value = input.value
                              ? input.value.toUpperCase()
                              : "";

                            // Restore the cursor position
                            setTimeout(() => {
                              input.setSelectionRange(
                                selectionStart,
                                selectionEnd
                              );
                            });

                            methods.setValue("roleCode", e.target.value, {
                              shouldValidate: true,
                            }); // UPDATE VALUE
                          },
                        })}
                        error={!!methods.formState.errors.roleCode}
                        className={`text-input ${
                          methods.formState.errors.roleCode ? "error" : ""
                        }`}
                      />
                    </SkeletonWrapper>
                  </FormControl>
                  <Typography className="validation-message">
                    {methods.formState.errors.roleCode?.message}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth>
                    <FormLabel
                      className={`fileds-label-text  ${
                        methods.formState.errors.roleName
                          ? "validation-message"
                          : ""
                      }`}
                    >
                      Role Name *
                    </FormLabel>
                    <SkeletonWrapper
                      isLoading={showSkeleton}
                      className="skeleton-input"
                    >
                      <CustomTextField
                        placeholder=""
                        autoCapitalize="none"
                        type="text"
                        fullWidth
                        size="small"
                        {...methods.register("roleName", {
                          required: true,
                          onChange: (e) => {
                            const input = e.target;
                            const selectionStart = input.selectionStart;
                            const selectionEnd = input.selectionEnd;

                            // CONVERT INPUT INTO UPPERCASE LATTERS
                            e.target.value = input.value
                              ? input.value.toUpperCase()
                              : "";

                            // Restore the cursor position
                            setTimeout(() => {
                              input.setSelectionRange(
                                selectionStart,
                                selectionEnd
                              );
                            });

                            methods.setValue("roleName", e.target.value, {
                              shouldValidate: true,
                            }); // UPDATE VALUE
                          },
                        })}
                        error={!!methods.formState.errors.roleName}
                        className={`text-input ${
                          methods.formState.errors.roleName ? "error" : ""
                        }`}
                      />
                    </SkeletonWrapper>
                  </FormControl>
                  <Typography className="validation-message">
                    {methods.formState.errors.roleName?.message}
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ display: "flex", justifyContent: "end", gap: 2 }}>
                <LoadingButton
                  color="primary"
                  loadingPosition="start"
                  startIcon={<></>}
                  variant="contained"
                  type="submit"
                  className="primary-button"
                  disabled={methods.formState.isSubmitting || isLoading}
                  roleName="save"
                >
                  {curUserGroupId > 0 ? "Update" : "Save"}
                </LoadingButton>
                <LoadingButton
                  color="primary"
                  loadingPosition="start"
                  startIcon={<></>}
                  variant="outlined"
                  type="button"
                  className="secondary-button"
                  disabled={methods.formState.isSubmitting || isLoading}
                  roleName="cancel"
                  onClick={handleBack}
                >
                  Cancel
                </LoadingButton>
              </Box>
            </Grid>
          </Grid>
        </form>
      </FormProvider>
    </Box>
  );
}
